import React, { useEffect, useRef, useState } from 'react';
import { ActivityIndicator, Animated, Easing, StyleSheet, View } from 'react-native';
import { Text } from './Texte';
import { MaterialCommunityIcons } from './icons';
import { t } from '../i18n';
import { colors, radius, spacing, typography } from '../theme/tokens';

/**
 * Attente d'une génération IA — honnête par construction.
 *
 * CE QU'IL NE FAIT PAS, et ne doit jamais faire : cocher des étapes sur une minuterie. L'écran de
 * création de CV le faisait — « Analyse de tes réponses », « Mise en forme », « Vérification ATS »
 * défilaient en 1,8 s quelle que soit la durée réelle. Quand l'appel prenait 25 secondes, la
 * personne regardait trois coches pleines devant un écran figé pendant 23 secondes : l'interface
 * affirmait que c'était fini alors que rien ne l'était.
 *
 * Le serveur ne renvoie AUCUN jalon intermédiaire : un seul appel Claude occupe ~95 % du temps.
 * Il n'y a donc rien d'interne à montrer, et ce composant n'offre aucune API pour le prétendre.
 *
 * CE QU'IL FAIT : le message évolue avec la DURÉE ÉCOULÉE, seule chose réellement connue. Aucune
 * de ces phrases ne devient fausse si l'appel prend une minute, et la dernière répond à la vraie
 * question — « est-ce que c'est planté ? ».
 *
 * Les `milestones` sont des jalons RÉELS, passés par l'appelant au moment où ils se produisent
 * (réponse reçue, document enregistré). Un jalon absent ne s'affiche pas ; il n'y a pas de moyen
 * d'en cocher un qui n'a pas eu lieu.
 *
 * `sections` EST UNE EXCEPTION ENCADRÉE, ajoutée pour l'audit LinkedIn. Elle fait défiler une ligne
 * de texte sur une minuterie — ce que le paragraphe ci-dessus proscrit — et la règle mérite donc
 * d'être reformulée plutôt que contournée :
 *
 *   CE QUI RESTE INTERDIT : afficher une étape comme FRANCHIE sans le savoir. Une coche, une barre
 *   qui se remplit, un « ✓ Titre analysé » sont des affirmations sur un état qu'on ne connaît pas.
 *
 *   CE QUI EST PERMIS : décrire, au présent progressif, ce que l'analyse PARCOURT. « J'analyse ton
 *   titre… » n'affirme aucun achèvement. Les libellés suivent l'ordre RÉEL des sections traitées par
 *   le prompt (`LINKEDIN_SYSTEM`), et la ligne s'arrête sur la dernière au lieu de boucler : revenir
 *   au début suggérerait un recommencement qui n'a pas lieu.
 *
 *   ET LA CONDITION QUI REND CET ARRÊT ACCEPTABLE : quand la séquence n'est pas pilotée par le
 *   serveur, LE DERNIER LIBELLÉ DOIT POUVOIR ÊTRE MAINTENU INDÉFINIMENT SANS DEVENIR FAUX. Neuf
 *   libellés de 3,5 s couvrent 31,5 s ; un appel de 50 s laisse donc vingt secondes sur le dernier.
 *   « Je vérifie la cohérence de l'ensemble… » serait alors devenu un mensonge par immobilité —
 *   exactement le reproche adressé aux coches, sous une autre forme. « Je finalise ton audit… »
 *   reste vrai à la seconde 31 comme à la seconde 90.
 *
 * L'appel Claude est unique et non streamé — vérifié dans `claude-linkedin/index.ts`, aucun
 * `stream: true` — donc rien de mieux n'est disponible. Le jour où la fonction émettra ses étapes,
 * `milestones` reprend la main et `sections` disparaît.
 */
export interface Milestone {
  key: string;
  label: string;
  done: boolean;
}

export interface GenerationProgressProps {
  /** Préfixe de clé du dictionnaire : `progress.<messageKey>.start|mid|long`. */
  messageKey: 'elapsed' | 'letter' | 'linkedin' | 'improve' | 'improveLetter';
  milestones?: Milestone[];
  /** Sections PARCOURUES, au présent progressif. Jamais de coche : voir l'en-tête du fichier. */
  sections?: string[];
  /**
   * Durée d'affichage d'une section. La cadence appartient à la SÉQUENCE, pas au composant : deux
   * séquences dont les durées vont du simple au triple ne peuvent pas partager une valeur sans que
   * l'une des deux mente — terminal jamais atteint d'un côté, ou trop bref de l'autre.
   */
  sectionMs?: number;
  testID?: string;
}

// Seuils de bascule du message. Choisis à l'estime : la mesure de durée (ai_usage_events.
// duration_ms, migration 0034) permettra de les caler sur des données réelles.
export const MID_MS = 15_000;
export const LONG_MS = 30_000;

/**
 * Durée d'affichage d'une section — CALÉE SUR UNE MESURE, pas sur une estimation.
 *
 * Relevé du 06/09/2026, table `ai_usage_events`, edge function `claude-linkedin` :
 *   mode `linkedin_account` (n = 27) : p50 87 541 ms, p90 92 833 ms, max 94 159 ms
 *   mode `linkedin_captures` (n = 5) : p50 87 736 ms, p90 94 854 ms, max 96 093 ms
 *
 * 11 s × 8 libellés avant le terminal = 88 s, soit la médiane. Le neuvième — « Je finalise ton
 * audit… » — prend le relais au-delà et tient SANS LIMITE, ce qui couvre le p90 comme le pire cas
 * observé. C'est ce qui rend l'arrêt sur le dernier libellé honnête : il ne devient jamais faux.
 *
 * À RE-MESURER si le prompt, le modèle ou `max_tokens` changent — les trois déplacent la durée. La
 * requête : `select percentile_cont(0.5) within group (order by duration_ms) from ai_usage_events
 * where feature like 'linkedin%'`.
 */
export const SECTION_MS = 11_000;

/**
 * Cadence de l'ÉTAPE 1 de l'audit LinkedIn (score et priorités), mesurée le 07/09/2026 :
 * 24 148 ms pour 931 tokens de sortie. Trois libellés à 8 s posent le terminal à 16 s, qui tient
 * donc huit secondes sur les vingt-quatre.
 *
 * Elle ne peut PAS valoir 11 s comme l'étape 2 : le terminal apparaîtrait à 22 s et ne tiendrait
 * que deux secondes — et jamais pour un audit descendu sous 22 s. Deux séquences de durées très
 * différentes ne peuvent pas partager une cadence.
 */
export const SECTION_MS_AUDIT = 8_000;

/** Palier de message à un instant donné. Exporté pour être testé sans horloge ni rendu. */
export function elapsedStage(elapsedMs: number): 'start' | 'mid' | 'long' {
  if (elapsedMs >= LONG_MS) return 'long';
  if (elapsedMs >= MID_MS) return 'mid';
  return 'start';
}

export function GenerationProgress({
  messageKey,
  milestones = [],
  sections = [],
  sectionMs = SECTION_MS,
  testID = 'generation-progress',
}: GenerationProgressProps) {
  const [stage, setStage] = useState<'start' | 'mid' | 'long'>('start');
  const [section, setSection] = useState(0);
  const pulse = useRef(new Animated.Value(0)).current;
  const fondu = useRef(new Animated.Value(1)).current;

  // Un seul intervalle, sur la durée écoulée depuis le montage. Rien ici ne dépend de l'avancement
  // du serveur, parce qu'on ne le connaît pas.
  useEffect(() => {
    const startedAt = Date.now();
    const id = setInterval(() => setStage(elapsedStage(Date.now() - startedAt)), 1000);
    return () => clearInterval(id);
  }, []);

  // Défilement des sections, avec arrêt sur la dernière. `setInterval` s'arrête de lui-même quand
  // l'index n'avance plus : rien ne tourne en fond une fois la fin atteinte.
  useEffect(() => {
    if (sections.length <= 1) return;
    const id = setInterval(() => {
      setSection((i) => {
        if (i >= sections.length - 1) return i;
        // Fondu : disparition puis réapparition, pour que le changement se remarque sans sauter.
        Animated.sequence([
          Animated.timing(fondu, { toValue: 0, duration: 180, useNativeDriver: true }),
          Animated.timing(fondu, { toValue: 1, duration: 320, useNativeDriver: true }),
        ]).start();
        return i + 1;
      });
    }, sectionMs);
    return () => clearInterval(id);
  }, [sections.length, sectionMs, fondu]);

  // Respiration continue : elle dit « ça travaille », sans jamais suggérer une position dans une
  // progression. Une barre qui avance mentirait sur l'avancement.
  useEffect(() => {
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(pulse, { toValue: 1, duration: 900, easing: Easing.inOut(Easing.ease), useNativeDriver: true }),
        Animated.timing(pulse, { toValue: 0, duration: 900, easing: Easing.inOut(Easing.ease), useNativeDriver: true }),
      ]),
    );
    loop.start();
    return () => loop.stop();
  }, [pulse]);

  const scale = pulse.interpolate({ inputRange: [0, 1], outputRange: [1, 1.12] });

  return (
    <View style={styles.container} testID={testID}>
      <Animated.View style={[styles.halo, { transform: [{ scale }] }]}>
        <ActivityIndicator size="large" color={colors.violet} />
      </Animated.View>

      <Text testID={`${testID}-message`} style={styles.message}>
        {t(`progress.${messageKey}.${stage}`)}
      </Text>

      {sections.length > 0 ? (
        <Animated.Text testID={`${testID}-section`} style={[styles.section, { opacity: fondu }]}>
          {sections[Math.min(section, sections.length - 1)]}
        </Animated.Text>
      ) : null}

      {milestones.length > 0 ? (
        <View style={styles.milestones} testID={`${testID}-milestones`}>
          {milestones.map(({ key, label, done }) => (
            <View key={key} style={styles.milestone} testID={`${testID}-milestone-${key}`}>
              <View style={[styles.dot, done && styles.dotDone]}>
                {done ? <MaterialCommunityIcons name="check" size={12} color={colors.white} /> : null}
              </View>
              <Text style={[styles.milestoneLabel, done && styles.milestoneLabelDone]}>{label}</Text>
            </View>
          ))}
        </View>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  container: { alignItems: 'center', gap: spacing.lg, paddingVertical: spacing.xxl },
  halo: {
    width: 72,
    height: 72,
    borderRadius: radius.pill,
    backgroundColor: colors.limeSoft,
    alignItems: 'center',
    justifyContent: 'center',
  },
  section: {
    fontSize: typography.small.size,
    lineHeight: typography.small.lineHeight,
    color: colors.grey500,
    textAlign: 'center',
    paddingHorizontal: spacing.lg,
  },
  message: {
    fontSize: typography.bodyMd.size,
    lineHeight: typography.body.lineHeight,
    color: colors.grey700,
    textAlign: 'center',
    paddingHorizontal: spacing.lg,
  },
  milestones: { gap: spacing.sm },
  milestone: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  dot: {
    width: 18,
    height: 18,
    borderRadius: radius.pill,
    borderWidth: 1.5,
    borderColor: colors.grey400,
    alignItems: 'center',
    justifyContent: 'center',
  },
  dotDone: { backgroundColor: colors.violet, borderColor: colors.violet },
  milestoneLabel: { fontSize: typography.small.size, color: colors.grey400 },
  milestoneLabelDone: { color: colors.grey700, fontWeight: '700' },
});
